/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ClipboardDocumentIcon,
  CloudIcon,
  ArrowPathIcon,
  ChartBarIcon,
  SparklesIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import Header from '../../components/layout/Header';
import ConfirmModal from '../../components/common/ConfirmModal';
import { finopsService } from '../../services/finopsApi';
import { formatDate, formatMicroUSD, COMMON_AWS_REGIONS } from '../../components/finops/finopsFormat';
import { useFinopsPermissions } from '../../hooks/useFinopsPermissions';
import { useOrgStore } from '../../store/orgStore';
import type { AWSConnection, ConnectionStatus } from '../../types/finops';

const STATUS_STYLES: Record<ConnectionStatus, string> = {
  pending: 'bg-yellow-500/20 text-yellow-400',
  active: 'bg-green-500/20 text-green-400',
  error: 'bg-red-500/20 text-red-400',
};

const StatusChip: React.FC<{ status: ConnectionStatus }> = ({ status }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[status]}`}>
    {status.charAt(0).toUpperCase() + status.slice(1)}
  </span>
);

const CopyBox: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => {
  const copy = () => {
    navigator.clipboard.writeText(value).then(
      () => toast.success(`${label} copied`),
      () => toast.error('Copy failed')
    );
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-gray-400">{label}</span>
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1 text-xs text-primary-400 hover:text-primary-300"
        >
          <ClipboardDocumentIcon className="w-3.5 h-3.5" />
          Copy
        </button>
      </div>
      <pre
        className={`bg-gray-950 border border-gray-800 rounded-lg p-3 text-xs text-gray-300 overflow-x-auto whitespace-pre-wrap break-all ${
          mono ? 'font-mono' : ''
        }`}
      >
        {value}
      </pre>
    </div>
  );
};

const ConnectPanel: React.FC<{ onCreated: () => void }> = ({ onCreated }) => {
  const [name, setName] = useState('');
  const [roleArn, setRoleArn] = useState('');
  const [regions, setRegions] = useState<string[]>(['us-east-1']);

  const createMutation = useMutation({
    mutationFn: () => finopsService.createConnection({ name: name.trim(), role_arn: roleArn.trim(), regions }),
    onSuccess: () => {
      toast.success('Connection created - set up the IAM role below, then verify');
      setName('');
      setRoleArn('');
      setRegions(['us-east-1']);
      onCreated();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Failed to create connection');
    },
  });

  const toggleRegion = (region: string) => {
    setRegions((prev) => (prev.includes(region) ? prev.filter((r) => r !== region) : [...prev, region]));
  };

  const canSubmit = name.trim().length > 0 && roleArn.trim().length > 0 && regions.length > 0 && !createMutation.isPending;

  return (
    <div className="card">
      <h3 className="text-lg font-semibold text-white mb-4">Connect AWS account</h3>
      <div className="space-y-4">
        <div>
          <label htmlFor="finops-conn-name" className="block text-sm font-medium text-gray-300 mb-2">
            Name
          </label>
          <input
            id="finops-conn-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Production AWS"
            className="input max-w-md"
          />
        </div>
        <div>
          <label htmlFor="finops-conn-role-arn" className="block text-sm font-medium text-gray-300 mb-2">
            Role ARN
          </label>
          <input
            id="finops-conn-role-arn"
            type="text"
            value={roleArn}
            onChange={(e) => setRoleArn(e.target.value)}
            placeholder="arn:aws:iam::123456789012:role/PodstackFinOpsReadOnly"
            className="input max-w-md font-mono text-sm"
          />
          <p className="text-xs text-gray-500 mt-1">
            You'll create this role after connecting - we'll show you the External ID and trust policy to paste in.
          </p>
        </div>
        <div>
          <span className="block text-sm font-medium text-gray-300 mb-2">Regions</span>
          <div className="flex flex-wrap gap-2">
            {COMMON_AWS_REGIONS.map((region) => {
              const selected = regions.includes(region);
              return (
                <button
                  key={region}
                  type="button"
                  onClick={() => toggleRegion(region)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-mono border transition-colors ${
                    selected
                      ? 'border-primary-500 bg-primary-500/10 text-primary-300'
                      : 'border-gray-700 bg-gray-800 text-gray-400 hover:bg-gray-750'
                  }`}
                >
                  {region}
                </button>
              );
            })}
          </div>
        </div>
        <button type="button" onClick={() => createMutation.mutate()} disabled={!canSubmit} className="btn-primary">
          {createMutation.isPending ? 'Connecting...' : 'Connect'}
        </button>
      </div>
    </div>
  );
};

const CreditsInlineForm: React.FC<{ connection: AWSConnection; onSaved: () => void }> = ({ connection, onSaved }) => {
  const [value, setValue] = useState<string>((connection.credits_total_micro_usd / 1_000_000).toFixed(2));

  const patchMutation = useMutation({
    mutationFn: () => finopsService.patchConnection(connection.id, { credits_total_usd: value }),
    onSuccess: () => {
      toast.success('Credits total updated');
      onSaved();
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to update credits total'),
  });

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-500">$</span>
      <input
        type="number"
        min={0}
        step="0.01"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="input py-1 px-2 text-sm w-28"
      />
      <button
        type="button"
        onClick={() => patchMutation.mutate()}
        disabled={patchMutation.isPending}
        className="btn-secondary btn-sm"
      >
        {patchMutation.isPending ? 'Saving...' : 'Set credits total'}
      </button>
    </div>
  );
};

const ConnectionCard: React.FC<{ connection: AWSConnection; onChanged: () => void }> = ({ connection, onChanged }) => {
  const { isManager, isOperator } = useFinopsPermissions();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const verifyMutation = useMutation({
    mutationFn: () => finopsService.verifyConnection(connection.id),
    onSuccess: (res) => {
      if (res.data.status === 'active') {
        toast.success('Connection verified');
      } else {
        toast.error(res.data.status_message || 'Verification failed');
      }
      onChanged();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.status_message || err?.response?.data?.error || 'Verification failed');
      onChanged();
    },
  });

  const syncMutation = useMutation({
    mutationFn: () => finopsService.syncInventory(connection.id),
    onSuccess: () => toast.success('Inventory sync started'),
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Sync already in progress'),
  });

  const syncCostsMutation = useMutation({
    mutationFn: () => finopsService.syncCosts(connection.id),
    onSuccess: () => toast.success('Cost sync started'),
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Sync already in progress'),
  });

  const analyzeMutation = useMutation({
    mutationFn: () => finopsService.analyze(connection.id),
    onSuccess: () => toast.success('Analysis started'),
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Analysis already in progress'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => finopsService.deleteConnection(connection.id),
    onSuccess: () => {
      toast.success('Connection deleted');
      setConfirmDelete(false);
      onChanged();
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to delete connection'),
  });

  return (
    <div className="card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-white">{connection.name}</h3>
            <StatusChip status={connection.status} />
          </div>
          <p className="text-xs text-gray-500 mt-1 font-mono">{connection.role_arn}</p>
          <p className="text-xs text-gray-500 mt-1">
            Regions: {connection.regions.join(', ')}
            {connection.account_id && ` · Account ${connection.account_id}`}
          </p>
          {connection.status === 'error' && connection.status_message && (
            <p className="text-xs text-red-400 mt-1">{connection.status_message}</p>
          )}
          <p className="text-xs text-gray-600 mt-1">
            Last inventory sync: {formatDate(connection.last_inventory_sync_at)} · Last cost sync:{' '}
            {formatDate(connection.last_cost_sync_at)}
          </p>
        </div>
        {isManager && (
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="text-gray-500 hover:text-red-400 transition-colors"
            title="Delete connection"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        )}
      </div>

      {connection.status !== 'active' && (
        <div className="mt-6 pt-6 border-t border-gray-800 space-y-4">
          <p className="text-sm text-gray-300">
            Create an IAM role in your AWS account that trusts Podstack using the External ID below, attach the
            role ARN you provided, then click Verify.
          </p>
          <CopyBox label="External ID" value={connection.external_id} mono />
          <CopyBox label="Trust policy" value={JSON.stringify(connection.trust_policy, null, 2)} />
          {isOperator && (
            <button
              type="button"
              onClick={() => verifyMutation.mutate()}
              disabled={verifyMutation.isPending}
              className="btn-primary"
            >
              {verifyMutation.isPending ? 'Verifying...' : "I've created the role - Verify"}
            </button>
          )}
        </div>
      )}

      <div className="mt-6 pt-6 border-t border-gray-800 flex flex-wrap items-center gap-3">
        {isOperator && (
          <button
            type="button"
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
            className="btn-secondary btn-sm inline-flex items-center gap-1.5"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Sync now
          </button>
        )}
        {isOperator && (
          <button
            type="button"
            onClick={() => syncCostsMutation.mutate()}
            disabled={syncCostsMutation.isPending}
            className="btn-secondary btn-sm inline-flex items-center gap-1.5"
          >
            <ChartBarIcon className="w-4 h-4" />
            Sync costs
          </button>
        )}
        {isOperator && (
          <button
            type="button"
            onClick={() => analyzeMutation.mutate()}
            disabled={analyzeMutation.isPending}
            className="btn-secondary btn-sm inline-flex items-center gap-1.5"
          >
            <SparklesIcon className="w-4 h-4" />
            Analyze
          </button>
        )}
        <span className="text-xs text-gray-500">
          Credits total: {formatMicroUSD(connection.credits_total_micro_usd)}
        </span>
        {isManager && <CreditsInlineForm connection={connection} onSaved={onChanged} />}
      </div>

      <ConfirmModal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => deleteMutation.mutate()}
        isLoading={deleteMutation.isPending}
        title="Delete connection"
        message={`Delete "${connection.name}"? This removes the connection and its inventory/recommendations from Podstack - nothing changes in your AWS account.`}
        confirmText="Delete connection"
        variant="danger"
      />
    </div>
  );
};

const Connections: React.FC = () => {
  const queryClient = useQueryClient();
  const { isManager } = useFinopsPermissions();
  const selectedOrgId = useOrgStore((s) => s.selectedOrgId);
  const orgName = useOrgStore((s) => s.organizations.find((o) => o.id === s.selectedOrgId)?.name);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['finops-connections'],
    queryFn: () => finopsService.listConnections().then((res) => res.data.connections),
  });

  const connections = data || [];
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['finops-connections'] });

  return (
    <div>
      <Header title="AWS Connections" subtitle="Connect a read-only AWS role to see cost, inventory and migration recommendations" />

      <div className="p-6 space-y-6">
        <p className="text-sm text-gray-400">
          {selectedOrgId ? `Shared with ${orgName ?? 'your organisation'}` : 'Personal (not shared)'}
        </p>

        {isManager && <ConnectPanel onCreated={refresh} />}

        {isLoading ? (
          <div className="card">
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-gray-700 border-t-primary-500 rounded-full animate-spin mb-4" />
              <p className="text-sm text-gray-400">Loading connections...</p>
            </div>
          </div>
        ) : isError ? (
          <div className="card">
            <div className="text-center py-12">
              <p className="text-red-400 mb-4">Failed to load connections</p>
              <button onClick={() => refetch()} className="btn-primary">
                Retry
              </button>
            </div>
          </div>
        ) : connections.length === 0 ? (
          <div className="card">
            <div className="text-center py-12">
              <CloudIcon className="mx-auto h-12 w-12 text-gray-600" />
              <h3 className="mt-2 text-sm font-medium text-white">No AWS connections yet</h3>
              <p className="mt-1 text-sm text-gray-400 max-w-md mx-auto">
                Connect an AWS account above to start tracking spend and getting migration recommendations.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {connections.map((c) => (
              <ConnectionCard key={c.id} connection={c} onChanged={refresh} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Connections;
